import type { FrameSpan } from './streetFrame';

/** A house front along a street, from s = `from` to s = `to` on the street's frame. */
export interface Front {
  /** The lot's key (lotKey.ts). */
  readonly key: string;
  readonly seed: number;
  readonly side: 'left' | 'right';
  readonly from: number;
  readonly to: number;
}

/**
 * The part of a street's frame one front paves, or a stretch with no house on either side (owner
 * null). Its spans run from cut to cut, where any front on either side starts or ends, so claims
 * that meet share their outlines vertex for vertex.
 */
export interface Claim {
  readonly owner: Front | null;
  readonly spans: readonly FrameSpan[];
}

// Fronts closer than this meet halfway: the lot planner leaves such gaps and overlaps at a bend of
// its source, while every real gap between houses, a gang or a failed lot, is a metre or more
// (gangen.ts GANG.width, lotRules.ts LOT.shrink.advanceStep).
const JOIN_GAP_M = 0.25;
const SAME_S = 1e-9;

function joined(fronts: readonly Front[], from: number, to: number): Front[] {
  const sorted = [...fronts].sort((a, b) => a.from - b.from);
  const meet = sorted.map((front, k) => {
    const next = sorted[k + 1];
    return next !== undefined && next.from - front.to < JOIN_GAP_M
      ? (front.to + next.from) / 2
      : null;
  });
  return sorted
    .map((front, k) => ({
      ...front,
      from: Math.max(from, meet[k - 1] ?? front.from),
      to: Math.min(to, meet[k] ?? front.to),
    }))
    .filter((front) => front.to - front.from > SAME_S);
}

function coverer(fronts: readonly Front[], s: number): Front | null {
  return fronts.find((front) => front.from < s && s < front.to) ?? null;
}

function cutsOf(
  fronts: readonly Front[],
  from: number,
  to: number,
  breaks: readonly number[]
): number[] {
  const all = [
    from,
    to,
    ...fronts.flatMap((f) => [f.from, f.to]),
    ...breaks.filter((s) => s > from && s < to),
  ].sort((a, b) => a - b);
  return all.filter((s, k) => k === 0 || s - all[k - 1]! > SAME_S);
}

interface Piece {
  readonly owner: Front | null;
  readonly span: FrameSpan;
}

/* Between two cuts, each built side's front paves out to the centre line, and across it where the
   other side has no house; with no house on either side the stretch is open. */
function piecesBetween(
  left: readonly Front[],
  right: readonly Front[],
  from: number,
  to: number,
  reach: number
): Piece[] {
  const middle = (from + to) / 2;
  const [l, r] = [coverer(left, middle), coverer(right, middle)];
  if (!l && !r) return [{ owner: null, span: { from, to, bottom: -reach, top: reach } }];
  return [
    ...(l ? [{ owner: l, span: { from, to, bottom: r ? 0 : -reach, top: reach } }] : []),
    ...(r ? [{ owner: r, span: { from, to, bottom: -reach, top: l ? 0 : reach } }] : []),
  ];
}

// The first of the open pieces running up to piece k.
function openRunStart(pieces: readonly Piece[], k: number): number {
  let first = k;
  while (first > 0 && pieces[first - 1]!.owner === null) first--;
  return first;
}

// Open pieces next to each other are one stretch; a front's pieces are its own.
function grouped(pieces: readonly Piece[]): Claim[] {
  const keys = pieces.map((piece, k) => piece.owner?.key ?? `open:${openRunStart(pieces, k)}`);
  return [...new Set(keys)].map((key) => {
    const own = pieces.filter((_, k) => keys[k] === key);
    return { owner: own[0]!.owner, spans: own.map((piece) => piece.span) };
  });
}

/**
 * The claims on a street's frame, which runs from `reach` before the street's start to `reach` past
 * its end and `reach` to either side: streetscape plan section 5.1. Every s in `breaks` cuts the
 * spans as well, so no span runs across one.
 */
export function claimsOf(
  fronts: readonly Front[],
  length: number,
  reach: number,
  breaks: readonly number[] = []
): readonly Claim[] {
  const [from, to] = [-reach, length + reach];
  const left = joined(
    fronts.filter((f) => f.side === 'left'),
    from,
    to
  );
  const right = joined(
    fronts.filter((f) => f.side === 'right'),
    from,
    to
  );
  const cuts = cutsOf([...left, ...right], from, to, breaks);
  const pieces = cuts
    .slice(1)
    .flatMap((cut, k) => piecesBetween(left, right, cuts[k]!, cut, reach));
  return grouped(pieces);
}
